import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { MARQUEE_PHRASES } from "@/data/books";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/**
 * Slow editorial marquee ribbon. Default (no `items` prop) renders the
 * bilingual Chinnu conversation-theme phrases, unchanged. Pass a plain
 * string array via `items` to show broad catalogue concepts instead
 * (used on the master /books hero — Command 02 §4: a scalable strip,
 * not a per-book ticker). Respects prefers-reduced-motion by rendering a
 * static wrapped line instead of an endless animation.
 */
export default function MarqueeRibbon({ tone = "dark", items }) {
  const dark = tone === "dark";
  const reducedMotion = usePrefersReducedMotion();
  const useConcepts = Array.isArray(items);
  const displayItems = useConcepts
    ? items.map((text) => ({ text, telugu: false }))
    : MARQUEE_PHRASES.flatMap((p) => [
        { text: p.en, telugu: false },
        { text: p.te, telugu: true },
      ]);

  const renderItem = (item, i) => (
    <span key={i} className="mx-7 inline-flex items-center gap-7 whitespace-nowrap">
      <span
        className={`${
          item.telugu ? "font-telugu" : useConcepts ? "font-semibold tracking-[0.14em] uppercase text-[13px] sm:text-[14px]" : "font-serif-d italic"
        } ${!useConcepts ? "text-[17px] sm:text-[19px]" : ""} ${dark ? "text-[#FBF7EE]/75" : "text-[#0D1B2A]/70"}`}
      >
        {item.text}
      </span>
      <span className="text-[10px]" style={{ color: "#F57F17" }}>◆</span>
    </span>
  );

  return (
    <div
      data-testid="marquee-ribbon"
      className={`overflow-hidden border-y select-none ${
        dark ? "bg-[#0A1522] border-white/10 py-5" : "bg-[#FBF7EE] border-[#E7DFCF] py-4"
      }`}
    >
      {reducedMotion ? (
        <div className="flex flex-wrap items-center justify-center gap-y-2 px-4" aria-hidden="true">
          {displayItems.map(renderItem)}
        </div>
      ) : (
        <Marquee speed={25} pauseOnHover gradient={false} aria-hidden="true">
          {displayItems.map(renderItem)}
        </Marquee>
      )}
    </div>
  );
}
